import { useState, useCallback } from 'react';
import { gatewayClient } from '../api';

/**
 * Hook for accessing the service-based Gateway Client
 * This replaces the old useGatewayClient hook with a cleaner architecture
 */
export const useGateway = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Generic request handler with loading states and error handling
   */
  const handleRequest = useCallback(async <T>(
    request: () => Promise<T>,
    options?: { rethrow?: boolean }
  ): Promise<T | null> => {
    setIsLoading(true);
    setError(null);

    try {
      const result = await request();
      return result;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unknown error occurred';
      setError(errorMessage);
      console.error('Gateway request failed:', err);
      if (options?.rethrow) {
        throw err;
      }
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    gateway: gatewayClient,
    isLoading,
    error,
    handleRequest,
    clearError: () => setError(null)
  };
};
